import React, {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Editor from 'react-simple-code-editor'
import { List, ListItemButton, ListItemText, useTheme } from '@mui/material'

import { useVariables } from '@hooks'
import { ENTITY_TYPES_REQUEST, HOT_KEY } from '@constants'
import { GETAllVariables } from '@types'

interface IProps {
  caretIndex: number
  inputValue: string
  isEditorFocused: boolean
  isShowList: boolean
  formId?: string | number
  windowId?: string | number
  caretCoordinates: {
    top: number
    left: number
    height: number
  }
  editorRef: RefObject<Editor>
  containerRef: MutableRefObject<HTMLDivElement | null>
  setIsShowList: (value: boolean) => void
  setCaretIndex: (value: number) => void
  onChangeInputValue: (value: string) => void
}

export const ListVariables = ({
  caretIndex,
  inputValue,
  isEditorFocused,
  isShowList,
  caretCoordinates,
  editorRef,
  formId,
  windowId,
  containerRef,
  setIsShowList,
  setCaretIndex,
  onChangeInputValue,
}: IProps) => {
  const globalVariables = useVariables({ isFetchAllVariables: true })
  const formVariables = useVariables({ id: formId, type: ENTITY_TYPES_REQUEST.FORM })
  const windowVariables = useVariables({ id: windowId, type: ENTITY_TYPES_REQUEST.WINDOW })

  const theme = useTheme()

  const [indexSelectedListItem, setIndexSelectedListItem] = useState(0)
  const [rightSpaceOverflow, setRightSpaceOverflow] = useState({ isOverflow: false, space: 0 })
  const [transformValue, setTransformValue] = useState('none')
  const [variables, setVariables] = useState<GETAllVariables>([])

  const variablesRef = useRef<Array<React.RefObject<HTMLDivElement>>>([])
  const listRef = useRef<HTMLUListElement | null>(null)

  const isIncorrectListPosition = useMemo(() => {
    return (
      editorRef?.current?._input.clientHeight < caretCoordinates?.top + caretCoordinates?.height
    )
  }, [caretCoordinates?.height, caretCoordinates?.top])

  const handleInsertVariable = (
    variableTitle: string,
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.preventDefault()

    const newString = inputValue.slice(0, caretIndex) + variableTitle + inputValue.slice(caretIndex)
    const newCaretIndex = (inputValue.slice(0, caretIndex) + variableTitle).length
      ? (inputValue.slice(0, caretIndex) + variableTitle).length
      : 0

    onChangeInputValue(newString)
    setIsShowList(false)

    if (editorRef && editorRef.current && editorRef.current._input) {
      editorRef.current._input.selectionStart = newCaretIndex
      editorRef.current._input.selectionEnd = newCaretIndex
    }

    setCaretIndex(newCaretIndex)
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isShowList) return

      if (event.key === HOT_KEY.ARROW_DOWN) {
        event.preventDefault()

        if (indexSelectedListItem === variables?.length - 1) return
        if (indexSelectedListItem > variables?.length - 1)
          return setIndexSelectedListItem(variables?.length - 1)

        setIndexSelectedListItem(prev => prev + 1)

        return
      }

      if (event.key === HOT_KEY.ARROW_UP) {
        event.preventDefault()

        if (indexSelectedListItem <= 0) return setIndexSelectedListItem(0)

        setIndexSelectedListItem(prev => prev - 1)

        return
      }

      if (event.key === HOT_KEY.ENTER) {
        if (variables[indexSelectedListItem]) {
          handleInsertVariable(variables[indexSelectedListItem].title, event)
        }
      }
    },
    [indexSelectedListItem, isShowList, variables]
  )

  useLayoutEffect(() => {
    if (!isShowList || !listRef.current || !containerRef.current) return

    const prevTransform = listRef.current.style.transform
    listRef.current.style.transform = 'none'

    const listPosition = listRef.current.getBoundingClientRect()
    const containerPosition = containerRef.current.getBoundingClientRect()

    const newSpace = containerPosition.right - listPosition.right
    const isOverflow = newSpace < 0

    listRef.current.style.transform = prevTransform

    if (rightSpaceOverflow.isOverflow !== isOverflow || rightSpaceOverflow.space !== newSpace) {
      setRightSpaceOverflow({ isOverflow, space: newSpace })
    }
  }, [isShowList, caretCoordinates])

  useLayoutEffect(() => {
    const valueTranslateX =
      containerRef?.current?.clientWidth && listRef?.current?.offsetWidth
        ? containerRef?.current?.clientWidth - listRef?.current?.offsetWidth
        : null
    const newTransform =
      isIncorrectListPosition && valueTranslateX ? `translateX(${valueTranslateX}px)` : 'none'

    if (newTransform === transformValue) return

    setTransformValue(newTransform)
  }, [
    caretCoordinates,
    containerRef?.current?.clientWidth,
    listRef?.current?.clientWidth,
    inputValue,
    rightSpaceOverflow.space,
  ])

  useEffect(() => {
    const refSelectItem = variablesRef?.current[indexSelectedListItem]

    if (!refSelectItem?.current) return

    refSelectItem.current.scrollIntoView({
      block: 'nearest',
    })
  }, [indexSelectedListItem])

  useEffect(() => {
    variablesRef.current = variables
      ? variables.map((_, index) => {
          return variablesRef.current[index] ?? React.createRef()
        })
      : variablesRef.current

    setIndexSelectedListItem(0)
  }, [variables])

  useEffect(() => {
    const lastWord = inputValue.trim().split(/\s+/).pop()

    if (lastWord && lastWord.startsWith('global')) {
      setVariables(globalVariables)
    }

    if (lastWord && lastWord.startsWith('form')) {
      setVariables(formVariables)
    }

    if (lastWord && lastWord.startsWith('window')) {
      setVariables(windowVariables)
    }
  }, [inputValue])

  useEffect(() => {
    const editorInput = editorRef.current?._input

    if (!editorInput) return

    editorInput.addEventListener('keydown', handleKeyDown)

    return () => editorInput.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const stylesForList = {
    padding: 0,
    position: 'absolute',
    background: '#fff',
    maxHeight: '100px',
    border: '1px solid black',
    overflow: 'auto',
    zIndex: 9999,
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '10px',
    },
    visibility: isShowList && isEditorFocused ? 'visible' : 'hidden',
    left:
      caretCoordinates?.left && isShowList && !isIncorrectListPosition
        ? `${caretCoordinates.left}px`
        : '0px',
    transform: rightSpaceOverflow?.isOverflow
      ? `translateX(${rightSpaceOverflow.space}px)`
      : transformValue,
    top:
      caretCoordinates?.top && !isIncorrectListPosition
        ? `${caretCoordinates.top + caretCoordinates.height}px`
        : `${caretCoordinates?.top ? caretCoordinates.top : 0}px`,
  }

  if (!Array.isArray(variables) || variables?.length === 0) return null

  return (
    <List ref={listRef} sx={stylesForList}>
      {variables.map((variable, index) => (
        <ListItemButton
          key={variable.id}
          ref={variablesRef.current[index]}
          selected={index === indexSelectedListItem}
          sx={{
            '&:not(:last-child)': {
              borderBottom: '1px solid black',
            },
            padding: '2px 10px',
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            },
          }}
          onMouseDown={event => handleInsertVariable(variable.title, event)}
          onMouseEnter={() => setIndexSelectedListItem(index)}
        >
          <ListItemText primary={variable.title} />
        </ListItemButton>
      ))}
    </List>
  )
}
